import { z } from "zod"

import {
  entityIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import { platformLifecycleStateV1Schema } from "./platform-workforce-v1.js"

const timestampSchema = z.string().datetime({ offset: true })

export const tenantLifecycleStateV1Schema = platformLifecycleStateV1Schema

export const tenantLifecycleReasonCodeV1Schema = z.enum([
  "prospect_created",
  "trial_started",
  "commercial_approved",
  "provisioning_ready",
  "trial_expired",
  "customer_requested",
  "payment_restriction",
  "policy_violation",
  "security_incident",
  "restriction_resolved",
  "offboarding_started",
  "offboarding_canceled",
  "offboarding_complete",
  "retention_elapsed",
  "deletion_approved",
])

export const tenantLifecycleChecksV1Schema = z
  .object({
    commercialReadiness: z.enum(["not_required", "ready", "blocked"]),
    provisioningReadiness: z.enum(["not_required", "ready", "blocked"]),
    subscriptionReadiness: z.enum([
      "not_required",
      "reconciled",
      "ambiguous",
      "restricted",
    ]),
    entitlementReadiness: z.enum(["not_required", "active", "inactive"]),
    routeReadiness: z.enum([
      "not_provisioned",
      "ready",
      "withdrawn",
      "mismatch",
    ]),
    healthReadiness: z.enum(["not_required", "ready", "degraded", "unknown"]),
    exportState: z.enum(["not_requested", "requested", "complete"]),
    providerDisconnectState: z.enum(["not_required", "pending", "complete"]),
    backupDispositionState: z.enum(["not_required", "pending", "complete"]),
    legalHoldState: z.enum(["none", "active"]),
    minimumRetainUntil: timestampSchema.nullable(),
    restorationWindowEndsAt: timestampSchema.nullable(),
    deletionEvidenceId: entityIdSchema.nullable(),
  })
  .strict()

export const tenantLifecycleSnapshotV1Schema = z
  .object({
    contractVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    state: tenantLifecycleStateV1Schema,
    reasonCode: tenantLifecycleReasonCodeV1Schema,
    version: z.number().int().nonnegative().safe(),
    effectiveAt: timestampSchema,
    checks: tenantLifecycleChecksV1Schema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine((snapshot, context) => {
    if (
      snapshot.state === "deleted"
      && snapshot.checks.deletionEvidenceId === null
    ) {
      context.addIssue({
        code: "custom",
        path: ["checks", "deletionEvidenceId"],
        message: "Deleted Tenant Lifecycle requires opaque deletion evidence.",
      })
    }
    if (Date.parse(snapshot.updatedAt) < Date.parse(snapshot.createdAt)) {
      context.addIssue({
        code: "custom",
        path: ["updatedAt"],
        message: "Tenant Lifecycle update cannot predate creation.",
      })
    }
  })

export const tenantLifecycleTransitionV1Schema = z
  .object({
    contractVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    expectedVersion: z.number().int().nonnegative().safe(),
    nextState: tenantLifecycleStateV1Schema,
    reasonCode: tenantLifecycleReasonCodeV1Schema,
    effectiveAt: timestampSchema,
    checks: tenantLifecycleChecksV1Schema,
  })
  .strict()

export const tenantLifecycleMutationResultV1Schema = z
  .object({
    responseVersion: z.literal(1),
    changed: z.boolean(),
    lifecycle: tenantLifecycleSnapshotV1Schema,
  })
  .strict()

export type TenantLifecycleChecksV1 = z.infer<
  typeof tenantLifecycleChecksV1Schema
>
export type TenantLifecycleMutationResultV1 = z.infer<
  typeof tenantLifecycleMutationResultV1Schema
>
export type TenantLifecycleReasonCodeV1 = z.infer<
  typeof tenantLifecycleReasonCodeV1Schema
>
export type TenantLifecycleSnapshotV1 = z.infer<
  typeof tenantLifecycleSnapshotV1Schema
>
export type TenantLifecycleStateV1 = z.infer<
  typeof tenantLifecycleStateV1Schema
>
export type TenantLifecycleTransitionV1 = z.infer<
  typeof tenantLifecycleTransitionV1Schema
>
